/**
 * The document trash on the app side: permanent deletion of trash entries,
 * the 30-day cleanup when a member who may edit opens the doc, emptying, and
 * the queued server restore of a cloud doc's tables and automations.
 *
 * In a cloud workspace tables and automations come back and leave through
 * the server, which changes the doc and their server state in one commit and
 * syncs back; every other entry, and every entry of a local workspace, stays
 * in the doc.
 */
import { toast } from '@affine/component';
import { I18n } from '@affine/i18n';
import type { Store } from '@blocksuite/affine/store';
import {
  findExpiredTrashItems,
  getTrashcan,
  permanentDeleteFromTrashcan,
  type TrashcanItem,
} from '@blocksuite/affine-block-streamlain';
import { Service } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import type { AutomationDeletionService } from './automation-deletion';
import type { DatabaseDeletionService } from './database-deletion';

export class DocTrashService extends Service {
  constructor(
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly deletion: DatabaseDeletionService,
    private readonly automationDeletion: AutomationDeletionService
  ) {
    super();
  }

  /**
   * Permanently delete the given entries of the doc.
   *
   * @returns Number of entries deleted locally or queued for server deletion
   */
  async permanentlyDelete(
    store: Store,
    items: readonly TrashcanItem[]
  ): Promise<number> {
    if (store.readonly) return 0;
    let deleted = 0;
    for (const item of items) {
      if (await this.deleteOne(store, item)) deleted++;
    }
    return deleted;
  }

  /** Entries older than 30 days leave for good. */
  cleanupExpired(store: Store): Promise<number> {
    return this.permanentlyDelete(store, findExpiredTrashItems(store));
  }

  /** „Papierkorb leeren“, after the user confirmed it. */
  empty(store: Store): Promise<number> {
    return this.permanentlyDelete(store, getTrashcan(store)?.props.items ?? []);
  }

  /**
   * A table, and an automation the server moved into the trash, of a cloud
   * workspace comes back through the server; every other entry (also an
   * automation moved in the doc, e.g. one never saved on the server), and every
   * entry of a local workspace, comes back in the doc.
   */
  restoresOnServer(item: TrashcanItem): boolean {
    if (this.workspaceServerService.server === null) return false;
    if (item.blockType === 'database') return true;
    return item.blockType === 'automation' && item.appLifecycle !== undefined;
  }

  /**
   * Queues the restore of a table or automation entry under its own
   * operation; the doc changes only by the sync of the committed host. A
   * table entry without a database id was never trashed by the server and
   * cannot come back through it.
   *
   * @returns the queued request, or null for a table entry without a
   *   database id and when it withdrew a trash not yet sent
   */
  async restoreOnServer(
    store: Store,
    item: TrashcanItem
  ): Promise<{ mutationId: string } | null> {
    if (item.blockType === 'automation') {
      return this.automationDeletion.requestRestore({
        docId: store.id,
        blockId: item.id,
      });
    }
    if (!item.databaseId) {
      toast(I18n.t('com.streamlain.trash.database-restore-unavailable'));
      return null;
    }
    return this.deletion.requestRestore({
      docId: store.id,
      blockId: item.id,
      databaseId: item.databaseId,
    });
  }

  private async deleteOne(store: Store, item: TrashcanItem): Promise<boolean> {
    if (!this.restoresOnServer(item)) {
      return item.blockType === 'database'
        ? false
        : permanentDeleteFromTrashcan(store, item.id);
    }
    try {
      if (item.blockType === 'automation') {
        await this.automationDeletion.requestPermanentDelete({
          docId: store.id,
          blockId: item.id,
        });
      } else {
        await this.deletion.requestPermanentDelete({
          docId: store.id,
          blockId: item.id,
          databaseId: item.databaseId,
        });
      }
      return true;
    } catch (error) {
      toast(
        item.blockType === 'automation'
          ? I18n.t('com.streamlain.trash.automation-delete-failed')
          : I18n.t('com.streamlain.streamlain.database-delete.server-failed', {
              error: error instanceof Error ? error.message : String(error),
            })
      );
      return false;
    }
  }
}
